<template>
	<div class="mb-3">
		<v-alert outlined text dense type="info" class="text-body-2 mb-3">
			<template v-if="riga.key === 'google'">
				La spesa si legge con le credenziali di <b>Google Ads - Keyword Planner</b> in <router-link to="/setting/seo">Impostazioni → Seo</router-link>: file JSON del service account e ID cliente. Il
				service account deve essere utente dell’account Google Ads con le campagne.
				<div v-if="!riga.configured" class="mt-1"><b>Credenziali non ancora configurate:</b> completale lì, poi torna qui ad attivare la lettura.</div>
			</template>
			<template v-else-if="riga.key === 'meta'">
				Legge la spesa delle campagne Facebook e Instagram con un <b>utente di sistema</b> di Meta Business: il suo token non scade.
				<ol class="mt-2 mb-0">
					<li>In <b>Meta Business &gt; Impostazioni &gt; Utenti &gt; Utenti di sistema</b> crea un utente di sistema (ruolo Dipendente basta).</li>
					<li>Da <b>Assegna risorse</b> dagli l'account pubblicitario con il permesso <b>Visualizza rendimento</b>.</li>
					<li><b>Genera nuovo token</b>: scegli la tua app, scadenza <b>Mai</b> e il permesso <b>ads_read</b>.</li>
					<li>Incolla qui il token e l'ID dell'account pubblicitario (in Gestione inserzioni, accanto al nome dell'account), premi <b>Conferma</b> e poi <b>Verifica connessione</b>.</li>
				</ol>
				<div class="mt-1">Il token resta cifrato sul server e non torna più in questa pagina.</div>
			</template>
			<template v-else-if="riga.key === 'tiktok'">
				<template v-if="riga.oauth && !riga.oauth.appConfigured"> <b>L’app TikTok di Azseller non è ancora configurata sul server:</b> il collegamento sarà disponibile appena attivata. </template>
				<template v-else-if="riga.oauth && riga.oauth.connected">
					TikTok è collegato<template v-if="riga.oauth.connectedAt"> dal {{ giornoData(riga.oauth.connectedAt) }}</template
					>. Scegli qui sotto l’account pubblicitario da leggere, premi <b>Conferma</b> e accendi l’interruttore. Per collegare altri account premi <b>Ricollega TikTok</b>.
				</template>
				<template v-else>
					Premi <b>Collega TikTok</b>: si apre il sito di TikTok for Business, entri con l’account che gestisce le campagne e autorizzi Azseller a leggere i report (solo lettura). Al ritorno scegli
					l’account pubblicitario e accendi la lettura.
				</template>
			</template>
			<template v-else-if="riga.key === 'microsoft'">
				<template v-if="riga.oauth && !riga.oauth.appConfigured">
					<b>L’app Microsoft Ads di Azseller non è ancora configurata sul server:</b> il collegamento sarà disponibile appena attivata.
				</template>
				<template v-else-if="riga.oauth && riga.oauth.connected">
					Microsoft Ads è collegato<template v-if="riga.oauth.connectedAt"> dal {{ giornoData(riga.oauth.connectedAt) }}</template
					>. Scegli qui sotto l’account pubblicitario da leggere, premi <b>Conferma</b> e accendi l’interruttore. L’autorizzazione resta valida finché la lettura gira almeno una volta ogni 90 giorni.
				</template>
				<template v-else>
					Premi <b>Collega Microsoft</b>: si apre l’accesso Microsoft, entri con l’utente che gestisce le campagne in Microsoft Advertising e autorizzi Azseller (solo lettura dei report). Al ritorno
					scegli l’account pubblicitario e accendi la lettura.
				</template>
			</template>
			<template v-else>Ogni notte Azseller legge la spesa delle campagne degli ultimi 30 giorni; alla prima attivazione anche gli ultimi 13 mesi.</template>
		</v-alert>
		<div v-if="riga.sync" class="d-flex align-center flex-wrap ai-status" style="gap: 8px">
			<v-chip x-small label outlined :color="colore">{{ etichetta }}</v-chip>
			<span v-if="riga.sync.syncedAt">ultima lettura {{ data(riga.sync.syncedAt) }}</span>
			<span v-if="riga.sync.backfilledFrom">· storico dal {{ giorno(riga.sync.backfilledFrom) }}</span>
		</div>
		<div v-else-if="riga.savedActive" class="ai-status">Lettura in coda.</div>
		<div v-if="riga.sync && riga.sync.status === 'error'" class="ai-error mt-1">{{ errore }}</div>
	</div>
</template>

<script>
// errori del client Google Ads (features/googleads/client.js) in parole
const ERRORS = {
	googleads_credentials: 'Il file JSON del service account non è valido o non è leggibile.',
	googleads_no_access: 'Il service account non ha accesso all’account Google Ads indicato: aggiungilo come utente dell’account.',
	googleads_api_disabled: 'La Google Ads API è spenta nel progetto Google Cloud del service account: va attivata.',
	googleads_project_not_approved: 'Il progetto Google Cloud non ha ancora l’accesso alla Google Ads API.',
	googleads_rate_limited: 'Google Ads ha rifiutato per troppe richieste: si riprova la notte prossima.',
	googleads_timeout: 'Google Ads non ha risposto in tempo.',
	googleads_unavailable: 'Google Ads non raggiungibile.',
	metaads_token: 'Il token di Meta non è valido, è scaduto o è stato revocato: generane uno nuovo dall’utente di sistema.',
	metaads_permission: 'Al token manca il permesso ads_read o l’utente di sistema non ha l’account pubblicitario.',
	metaads_account: 'Account pubblicitario Meta non trovato o non accessibile con questo token.',
	metaads_rate_limited: 'Meta ha rifiutato per troppe richieste: si riprova la notte prossima.',
	metaads_timeout: 'Meta non ha risposto in tempo.',
	metaads_unavailable: 'Meta non raggiungibile.',
	microsoftads_token: 'L’autorizzazione di Microsoft non è più valida (scade se non usata per 90 giorni o se revocata): premi Ricollega Microsoft.',
	microsoftads_permission: 'L’utente Microsoft non ha accesso all’account pubblicitario o al developer token: ricollega con l’utente giusto.',
	microsoftads_rate_limited: 'Microsoft ha rifiutato per troppe richieste: si riprova la notte prossima.',
	microsoftads_timeout: 'Microsoft non ha risposto in tempo o il report non è stato pronto in 10 minuti.',
	microsoftads_unavailable: 'Microsoft non raggiungibile.',
	tiktokads_token: 'L’autorizzazione di TikTok non è più valida o è stata revocata: premi Ricollega TikTok.',
	tiktokads_permission: 'L’autorizzazione di TikTok non permette di leggere i report: ricollega TikTok accettando tutti i permessi.',
	tiktokads_rate_limited: 'TikTok ha rifiutato per troppe richieste: si riprova la notte prossima.',
	tiktokads_timeout: 'TikTok non ha risposto in tempo.',
	tiktokads_unavailable: 'TikTok non raggiungibile.',
	ads_not_configured: 'Collegamento non configurato.',
};

// istruzioni ed esito dell'ultima lettura di una piattaforma, dentro il riquadro di Impostazioni -> Pubblicita'
export default {
	name: 'AdsSourceInfo',
	props: {
		riga: { type: Object, required: true },
	},
	computed: {
		etichetta() {
			return { ok: 'Letta', error: 'Errore', running: 'In lettura' }[this.riga.sync.status] || this.riga.sync.status;
		},
		colore() {
			return { ok: 'green darken-1', error: 'red darken-1', running: 'primary' }[this.riga.sync.status] || 'grey';
		},
		errore() {
			const code = String(this.riga.sync.error || '');
			const key = code.split(':')[0];
			return ERRORS[key] ? `${ERRORS[key]}${code.includes(':') ? ` (${code.slice(code.indexOf(':') + 1).trim()})` : ''}` : code;
		},
	},
	methods: {
		data: d => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
		giorno: d => String(d).split('-').reverse().join('/'),
		giornoData: d => new Date(d).toLocaleDateString('it-IT'),
	},
};
</script>

<style scoped>
.ai-status {
	font-size: 13px;
	color: #5f6b7a;
}
.ai-error {
	font-size: 13px;
	color: #c62828;
}
</style>
